/**
 * Componente File que permite llevar acabo todas las operaciones sobre los archivos
 */

import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFirestore, useFirebase } from 'react-redux-firebase';
import { Drawer, Form, Button, Tabs, Col, Row, Divider, notification, Modal, Table, Popconfirm, Select, Input, DatePicker, InputNumber, Slider } from 'antd';
import { DownloadOutlined, DeleteOutlined, EyeOutlined, LeftCircleTwoTone, RightCircleTwoTone, CheckCircleOutlined, SlidersTwoTone } from '@ant-design/icons';
import moment from 'moment';
//import { Document, Page, pdfjs } from 'react-pdf';
import { Worker, Viewer, RotateDirection } from '@react-pdf-viewer/core';
import { pageNavigationPlugin } from '@react-pdf-viewer/page-navigation';
import { XMLUpload, AccountingUpload, LinkUpload } from './upload';
import { references as referencesActions } from '../../actions/database/references';
import { files as filesActions } from '../../actions/database/documents';
import { REFERENCES, DOCUMENTS } from '../../../application/types';
import { sanitizeForFirestore } from '../../lib/firestore_safe';
import { toAccountingList, hasAccounting, ONE_ACCOUNTING_MESSAGE } from '../../lib/accounting_refs';
import { canSeeAccountedMark } from '../../lib/accounted_visibility';
import { canDeleteLinked, canDeleteAccounting, canDeleteWithoutToken, withNoTokenGrant } from '../../lib/protected_delete_perms';
import { closedFolderAccess, isSupervisor } from '../../lib/closed_folder_perms';
import { CLIENT_ROLE_IDS } from '../../lib/role_seed';
import { DeleteTokenModal } from './delete_token_modal';
import { RequestDeletionModal, RequestDeletionControl } from './request_deletion';
import { deletionRequests } from '../../actions/database/deletion_requests';

import { pendingKeyFor } from '../../lib/deletion_request';
import { DELETION_ITEM_TYPES } from '../../lib/deletion_token';
import { extractCustomerId } from '../../actions/database/trash';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { useDocuments } from './context';

import { zoomPlugin } from '@react-pdf-viewer/zoom';
import { rotatePlugin } from '@react-pdf-viewer/rotate';
import '@react-pdf-viewer/zoom/lib/styles/index.css';
import '@react-pdf-viewer/page-navigation/lib/styles/index.css';
//pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

/**
 * Sub Componente que representa la descripción del archivo
 * @param {object} param0 
 */
const DescriptionItem = ({ title, content }) => (
  <div style = {{ fontSize: 14, lineHeight: '22px', marginBottom: 7, color: 'rgba(0,0,0,0.65)' }} >
    <p style = {{ marginRight: 8, display: 'inline-block', color: 'rgba(0,0,0,0.85)' }} >
      {title}:
    </p>
    {content}
  </div>
);

/**
 * Previsualización de un PDF a pantalla completa, con su barra de paginación,
 * zoom y rotación.
 *
 * Es un componente propio y no JSX repetido porque cada visor necesita SUS
 * PROPIAS instancias de plugin: las de @react-pdf-viewer se enlazan a un solo
 * <Viewer>, así que compartirlas entre dos previsualizaciones no funciona.
 *
 * El título es solo el nombre del archivo. Antes intentaba mostrar
 * "Pagina X de Y" con `preview.pageNumber`/`numPages`, que quedaron sin
 * actualizarse desde la migración de react-pdf a @react-pdf-viewer
 * (bc8d28e, 2024-08-16) y siempre decían 1 de 1. La página real la muestra
 * <CurrentPageLabel /> en la barra de abajo, que sí viene del plugin.
 */
const PdfPreviewDrawer = ({ visible, title, url, onClose }) => {

  const pageNavigationPluginInstance = pageNavigationPlugin();
  const { jumpToNextPage, jumpToPreviousPage, CurrentPageLabel, NumberOfPages } = pageNavigationPluginInstance;

  const zoomPluginInstance = zoomPlugin();
  const { zoomTo } = zoomPluginInstance;

  const rotatePluginInstance = rotatePlugin();
  const { Rotate } = rotatePluginInstance;

  return (
    <Drawer
      forceRender
      title = { title }
      width = '100%'
      placement = "left"
      onClose = { onClose }
      maskClosable = { false }
      visible = { visible }
      style = {{ height: 'calc(100% - 55px)', overflow: 'auto', paddingBottom: 53 }}
      destroyOnHidden = { true } >

      <div style = {{ height: '750px' }} >
        { url ?
          <Worker workerUrl = "https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js" >
            <Viewer
              fileUrl = { url }
              plugins = {[ pageNavigationPluginInstance, zoomPluginInstance, rotatePluginInstance ]} />
          </Worker> : null }
      </div>

      <div style = {{
          position: 'absolute',
          bottom: 0,
          width: '100%',
          borderTop: '1px solid #e8e8e8',
          padding: '10px 16px',
          textAlign: 'left',
          left: 0,
          background: '#fff',
          borderRadius: '0 0 4px 4px',
        }} >
        <Row>
          <Col span = { 12 } >
            <Button style = {{ marginRight: 8 }} onClick = { jumpToPreviousPage } >
              <LeftCircleTwoTone /> Página anterior
            </Button>

            <Button style = {{ marginRight: 8 }} onClick = { jumpToNextPage } >
              Siguiente página <RightCircleTwoTone />
            </Button>

            {/* Gira el documento 90 grados por clic, como el boton que existia
                antes de la migracion del visor. */}
            <Rotate direction = { RotateDirection.Forward } >
              { props => (
                <Button style = {{ marginRight: 8 }} onClick = { props.onClick } >
                  <SlidersTwoTone /> Rotar
                </Button>
              )}
            </Rotate>

            <div style = {{ display: 'inline-block', marginLeft: 8 }} >
              <CurrentPageLabel /> / <NumberOfPages />
            </div>
          </Col>
          <Col span = { 12 } >
            <Slider
              min = { 1 }
              max = { 5 }
              step = { 0.1 }
              marks = {{ 1: 'Zoom:' }}
              onChange = { value => zoomTo(value) } />
          </Col>
        </Row>
      </div>

    </Drawer>
  );
};

/**
 * Exporta el componente File
 */
export const File = () => {

  // Funcion que permite ejecutar todas las acciones del estado de redux
  const dispatch = useDispatch();
  // Selecciona los reductores
  const read = useSelector(state => state.get('references').get('read'));
  const auth = useSelector(state => state.get('authenticate').get('login'));
  // Rol global del usuario (para el permiso por rol de root/contador,
  // independiente del efectivo por carpeta).
  const globalRole = useSelector(state => state.get('roles').get('roles').roles);
  // La fecha de registro de referencias y vínculos es interna: no la ven los clientes.
  const showRegistrationDate = !!globalRole?.id && !CLIENT_ROLE_IDS.includes(globalRole.id);

  // Hook para ejecutar funciones de firebase
  const firestore = useFirestore();
  const firebase = useFirebase();

  // Manejo de los estados del componente
  const [selected, setSelected] = useState({ type: "", message: "", component: undefined });
  const [xml, setXml] = useState({ loading: false, source: [] });
  const [accounting, setAccounting] = useState({ loading: false, source: [] });
  const [references, setReferences] = useState({ loading: false, visible: false, source: [] });
  const [links, setLinks] = useState({ loading: false, source: [] });
  // Borrado protegido por token (contabilización, vinculado, XML).
  // Guarda qué se va a borrar y CÓMO borrarlo; el modal solo ejecuta `run`
  // después de que el backend valide el token. Ver lib/deletion_token.js.
  const [tokenDelete, setTokenDelete] = useState(null);
  // Solicitud de eliminación (quien no es supervisora pide permiso en vez de
  // borrar). pendingRequests: ruta del adjunto → {id, requestedBy}.
  const [requestDelete, setRequestDelete] = useState(null);
  const [pendingRequests, setPendingRequests] = useState(new Map());
  const [approve, setApprove] = useState({ loading: false, source: [] });
  // numPages/pageNumber/rotate/scale eran del visor viejo (react-pdf); desde
  // la migracion los maneja el propio @react-pdf-viewer.
  const [preview, setPreview] = useState({ visible: false });
  // Previsualización de un archivo de contabilización (PDF) de la tabla del tab.
  const [accPreview, setAccPreview] = useState({ visible: false, url: null, name: '' });

  // Hook para el control de los formularios
  const [form] = Form.useForm();

  const { folders } = useDocuments();
  const { file, user, isClosing } = folders.current;
  // Qué permite el cierre de la carpeta a este usuario (lib/closed_folder_perms.js):
  // supervisora/root todo; contador/admin solo el cajón; nadie borra salvo
  // supervisora/root. Reemplaza el `(!isClosing || level === 'all')` que se
  // repetía en cada permiso.
  const closedAccess = closedFolderAccess({ globalRole, isClosing: isClosing?.data });

  const supervisor = isSupervisor(globalRole);

  // Eliminar sin token (spec 2026-10-05): root lo concede por usuario y por
  // tipo. Con el flag, botón directo con confirmar; sin token ni solicitud.
  const noToken = ( kind ) => canDeleteWithoutToken({
    kind,
    globalRole,
    isClosing: isClosing?.data,
    hasAccounting: kind === 'document' && hasAccounting( file.fields?.accounting ),
    accounted: kind === 'document' && !!file.fields?.accounted,
  });
  const confirmDirect = ( title, run ) => Modal.confirm({
    title,
    content: 'Se eliminará y quedará en la papelera.',
    okText: 'Eliminar',
    okType: 'danger',
    cancelText: 'Cancelar',
    onOk: () => run(),
  });
  const myUid = firebase.auth().currentUser?.uid;
  const parentPath = file.reference?.ref?.path;

  // Solicitudes pendientes de ESTE documento, desde el índice
  // deletion_request_pending (lo lee cualquiera: todos ven lo que ya está
  // pedido, no solo lo suyo). Dos fuentes: los adjuntos del cajón (su
  // parentPath es este documento) y el documento mismo (su clave).
  // Cuando una pendiente desaparece (se aprobó, rechazó o canceló), se
  // recargan las listas del cajón para no mostrar algo que ya se borró.
  const pendingSeen = useRef(new Set());
  useEffect(() => {
    if (!parentPath || !myUid) { setPendingRequests(new Map()); return undefined; }
    const db = firebase.firestore();
    let fromItems = new Map();
    let fromDoc = new Map();
    const publish = () => {
      const merged = new Map([...fromItems, ...fromDoc]);
      const gone = [...pendingSeen.current].some((k) => !merged.has(k));
      pendingSeen.current = new Set(merged.keys());
      setPendingRequests(merged);
      if (gone) { loadXML(); loadAccounting(); loadLinksFiles(); }
    };
    const toEntry = (d) => [d.data().itemPath, { id: d.data().requestId, requestedBy: d.data().requestedBy }];
    const offItems = db.collection('deletion_request_pending')
      .where('parentPath', '==', parentPath)
      .onSnapshot((snap) => { fromItems = new Map(snap.docs.map(toEntry)); publish(); }, () => {});
    const offDoc = db.collection('deletion_request_pending').doc(pendingKeyFor(parentPath))
      .onSnapshot((d) => { fromDoc = d.exists ? new Map([toEntry(d)]) : new Map(); publish(); }, () => {});
    return () => { offItems(); offDoc(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentPath, myUid]);

  // Arma lo que necesita el modal de solicitud para una fila del cajón.
  // `tokenRun` solo si hoy podría borrar directo (carpeta abierta): así el
  // enlace "Tengo el token" no aparece donde el token tampoco serviría.
  const requestFor = ({ kind, record, itemPath, tokenItemType, tokenRun }) => ({
    kind,
    itemName: record?.name,
    itemPath,
    itemUrl: record?.url || null,
    parentPath,
    parentName: file.fields?.name,
    customerId: extractCustomerId(file.fields?.folder?.path),
    customerName: file.fields?.customer,
    tokenItemType,
    tokenRun: closedAccess.drawerDelete ? tokenRun : null,
  });

  const renderRequestControl = (req) => {
    const pending = pendingRequests.get(req.itemPath);
    return (
      <>
        <Divider type = "vertical" />
        <RequestDeletionControl
          pending = { !!pending }
          canCancel = { pending?.requestedBy === myUid }
          onRequest = { () => setRequestDelete(req) }
          onCancelPending = { () => dispatch(deletionRequests.cancel({ id: pending.id })).catch(() => {
            notification.error({ message: 'Error', description: 'No se pudo cancelar la solicitud', placement: 'topRight' });
          }) }
        />
      </>
    );
  };

  /**
   * Metodo del componente que se ejecuta una vez se cierre la ficha tecnica del archivo
   * Incializa nuevamente las variables del componente
   */
  const onCloseFile = () => {
    form.resetFields();
    file.set({ visible: false, isLoad: false, reference: undefined, fields: {} });
    setSelected("");
    setXml({ loading: false, source: [] });
    setAccounting({ loading: false, source: [] });
    setReferences({ loading: false, visible: false, source: [] });
    setLinks({ loading: false, source: [] });
  };

  /**
   * Metodo del componente que permite eliminar un archivo
   */
  const deleteFile = ( rolesOverride ) => {
    const fileId = file.reference?.id;
    dispatch( filesActions.delete({ roles: rolesOverride || user.roles.documents, reference: file.reference?.ref }) ).then( () => {
      onCloseFile();
      // Quitar la fila en sitio en vez de recargar la carpeta entera.
      if (fileId) folders.list.removeRow( fileId );
    }).catch( error => {
      notification.error({
        message: 'Error',
        description: 'Se presento un error al tratar de eliminar el archivo',
        placement:  'topRight'
      })
    });
  };

  /**
   * Metodo del componente que permite cargar el archivo XML subido
   */
  const loadXML = () => {
    setXml({ loading: true, source: [] });
    file.reference.ref.get().then( doc => {

      const { set, ...rest } = file;
      const fields = doc.data();

      set({ ...rest, fields });
      setXml({ loading: fields?.xml ? true : false, source: [] }); 

      if (!fields?.xml) return;
      fields.xml.get().then( file => {
        setXml({
          loading: false,
          source: file.exists ? [{
            key: file.id,
            name: file.data().name,
            url: file.data().url,
            date: file.data().date_of_upload.toDate().toLocaleDateString('es-CO', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })
          }] : []
        });
      });

    });
  };

  /**
   * Metodo del componente que permite cargar los archivos de contabilización.
   * El campo admite varias (array) y tolera el formato legacy (ref suelta).
   */
  const loadAccounting = () => {
    setAccounting({ loading: true, source: [] });
    file.reference.ref.get().then( doc => {

        const { set, ...rest } = file;
        const fields = doc.data();

        set({ ...rest, fields });

        const list = toAccountingList( fields?.accounting );

        // Al subir el soporte, el thunk `accounting` marca el archivo solo
        // (upload.js). Hay que llevar `accounted` a la fila del listado, no
        // solo `hasAccounting`: sin eso la marca aparecia unicamente tras
        // recargar la pagina.
        folders.list.patchRow( file.reference.id, {
          hasAccounting: list.length > 0,
          accounted: fields?.accounted || null,
        } );

        if (!list.length) return setAccounting({ loading: false, source: [] });

        Promise.all( list.map( ref => ref.get().catch( () => null ) ) ).then( snaps => {
          setAccounting({
            loading: false,
            source: snaps.filter( s => s?.exists ).map( s => ({
              key: s.id,
              name: s.data().name,
              url: s.data().url,
              type: s.data().type, // para decidir cómo previsualizar
              ref: s.ref, // necesario para reversar ESTA contabilización
              date: s.data().date_of_upload?.toDate ? s.data().date_of_upload.toDate().toLocaleDateString('es-CO', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : ''
            }))
          });
        });

    });
  };

  /**
   * Metodo del componente que permite cargar las referencias
   */
  const loadReferences = () => {
    setReferences({ loading: true, source: [], visible: false });
    file.reference.ref.collection('references').get().then( query => {

      let source = [];
      setReferences({ loading: !query.empty ? true : false, source: [], visible: false });
      
      if (query.empty) return;
      query.docs.forEach( doc => {

        const data = doc?.data();
        source.push({
          key: `${ data?.name }-${ data?.value }`,
          index: {
            name: data?.name,
            value: data?.value
          },
          ref: data?.name,
          value: typeof data?.value !== "object" ? data?.value : data?.value?.toDate().toLocaleDateString('es-CO', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          // Las referencias anteriores a este campo no guardaron fecha.
          date: data?.createdAt?.toDate ? data.createdAt.toDate().toLocaleString('es-CO', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }) : 'Sin registro'
        });

      });

      setReferences({ loading: false, visible: false, source });

    });
  };

  /**
   * Metodo del componente que permite cargar los usuarios aprobadores
   */
  const loadApprove = () => {
    setApprove({ loading: true, source: [] });
    file.reference.ref.get().then( async doc => {

      const { set, ...rest } = file;
      const fields = doc.data();
      let approves = [];

      set({ ...rest, fields });
      for ( const approve of Array.isArray( fields?.approve ) ? fields.approve : [] ) {
        const data = ( await file.reference.ref.collection('approve').doc( approve ).get() ).data();
        if ( data ) {
          const user = ( await data?.user?.get() ).data()
          approves.push({
            name: user['Nombre completo'],
            email: user['Email'],
            date: data?.date_of_approve
          });
        }
      }

      setApprove({
        loading: false,
        source: approves.map( ( approve, index ) => {
          return {
            key: index,
            index: index,
            name: approve?.name,
            email: approve?.email,
            date: approve?.date?.toDate().toLocaleDateString('es-CO', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })
          }
        })
      });

    });
  };

  /**
   * Metodo del componente que permite cargar los archivos vinculados
   */
  const loadLinksFiles = () => {
    setLinks({ loading: true, source: [] });
    file.reference.ref.get().then( async doc => {
      
      const { set, ...rest } = file;
      const fields = doc.data();

      set({ ...rest, fields });
      setLinks({ loading: Array.isArray( fields?.links ) ? true : false, source: [] });
      
      if (!Array.isArray( fields?.links )) return;

      const source = [];
      for ( const link of fields?.links ) {

        const file = await link?.get();
        if ( file?.exists ) source.push({
          key: file?.id,
          ref: file?.ref,
          name: file?.data()?.name,
          url: file?.data()?.url,
          // El vínculo se crea al subir el archivo: date_of_upload es la fecha del vínculo.
          date: file?.data()?.date_of_upload?.toDate ? file.data().date_of_upload.toDate().toLocaleString('es-CO', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }) : 'Sin registro'
        });

      }
      
      setLinks({ loading: false, source });

    });
  };

  /**
   * Metodo del componente que permite reversar UNA contabilización del archivo
   * @param {object} record fila de la tabla (trae la ref del doc contable)
   */
  const accountingThrowBack = ( record, rolesOverride ) => {
    dispatch(
      filesActions.accountingThrowBack({
        roles: rolesOverride || user.roles.documents,
        reference: file.reference?.ref,
        accountingRef: record?.ref,
      }
    )).then( () => {
      form.resetFields();
      loadAccounting();
    }).catch( error => {
      notification.error({
        message: 'Error',
        description: 'Se presento un error al tratar de reversar la contabilización',
        placement:  'topRight'
      })
    });
  };

  /**
   * Marca/desmarca el archivo como contabilizado y relee el doc para
   * refrescar la etiqueta del encabezado. También recarga el listado de
   * la carpeta para que el Tag verde "Contabilizado" de la fila aparezca
   * o desaparezca sin cerrar el drawer (mismo mecanismo que deleteFile).
   */
  const toggleAccounted = () => {
    const action = file.fields?.accounted ? filesActions.unmarkAccounted : filesActions.markAccounted;
    dispatch( action({ roles: user.roles.documents, reference: file.reference?.ref }) ).then( () => {
      file.reference.ref.get().then( doc => {
        const { set, ...rest } = file;
        set({ ...rest, fields: doc.data() });
        // Solo cambia el tag de UNA fila: se parcha en sitio. Antes esto
        // recargaba la carpeta completa (y ahora ademas invalidaria la cache
        // de sesion y rehidrataria la pagina).
        folders.list.patchRow( file.reference.id, { accounted: doc.data()?.accounted || null } );
      });
    }).catch( () => {
      notification.error({
        message: 'Error',
        description: 'No se pudo actualizar la marca de contabilizado',
        placement: 'topRight'
      });
    });
  };

  /**
   * Metodo que se ejecuta cuando se cambia de pestaña y carga la información correspondiente
   * @param {object} key
   */
  const onChangeTab = key => {
    switch (key) {
      case "1": loadXML(); break;
      case "2": loadAccounting(); break;
      case "3": loadReferences(); break;
      case "4": loadLinksFiles(); break;
      case "5": loadApprove(); break;
      default: loadXML(); break;
    }
  };

  /**
   * Metodo del componente que elimina el archivo XML
   */
  const deleteXML = ( rolesOverride ) => {
    dispatch(
      filesActions.deleteXML({
        roles: rolesOverride || user.roles.documents,
        reference: file.reference?.ref,
      }
    )).then( () => loadXML() ).catch( error => {
      notification.error({
        message: 'Error',
        description: 'Se presento un error al tratar de eliminar el archivo XML',
        placement:  'topRight'
      })
    });
  };

  /**
   * Metodo que permite eliminar un archivo vinculado
   * @param {object} ref 
   */
  const deleteLinkFile = ( ref, rolesOverride ) => {
    dispatch(
      filesActions.deleteLink({
        roles: rolesOverride || user.roles.documents,
        reference: file.reference?.ref,
        link: ref
      }
    )).then( () => loadLinksFiles() ).catch( error => {
      notification.error({
        message: 'Error',
        description: 'Se presento un error al tratar de eliminar el archivo',
        placement:  'topRight'
      })
    });
  };

  /**
   * Metodo que permite abrir una ventana emergente para agregar referencias
   */
  const openDrawerReference = () => {
    form.resetFields();
    setReferences({ loading: true, visible: false, source: references.source });
    dispatch( referencesActions.read({ roles: user.roles.documents?.references }) ).then(
      () => setReferences({ loading: true, visible: true, source: references.source })
    ).catch(
      error => setReferences({ loading: false, visible: false, source: references.source })
    )
  };

  /**
   * Metodo que permite cerrar la ventana emergente de referencias
   */
  const closeDrawerReference = () => {
    form.resetFields();
    setReferences({ loading: false, visible: false, source: references.source });
  };

  /**
   * Metodo que se ejecuta cuando se selección una referencia y carga el tipo de dato
   * @param {object} value 
   */
  const handleSelectChangeReference = value => {
    const ref = read?.reference?.filter( ref => ref.id === value )[0];
    switch ( ref?.type ) {
      case "Numérico":
        setSelected({ type: "number", message: "Debe ingresar un valor númerico", component: <InputNumber style = {{ width: '100%' }} /> });
        form.setFieldsValue({ value: 0 });
        break;
      case "Alfanumérico":
        setSelected({ type: "string", message: "Debe ingresar un valor alfanúmerico", component: <Input style = {{ width: '100%' }} /> });
        form.setFieldsValue({ value: "" });
        break;
      case "Fecha":
        setSelected({ type: "object", message: "Debe ingresar una fecha", component: <DatePicker format = 'DD/MM/YYYY' style = {{ width: '100%' }} /> });
        form.setFieldsValue({ value: moment() });
        break;
      default: break;
    }
  };

  /**
   * Metodo que permite guardar una referencia
   */
  const saveReference = values => {
    
    const val = read?.reference?.filter( ref => ref.id === values.reference )[0];    
    file.reference.ref.get().then( doc => {

      let value = values.value;
      let collection = doc.ref.collection('references');

      if ( val?.type === "Fecha" ) {
        value = value.toDate();
        value.setHours(0);
        value.setMinutes(0);
        value.setSeconds(0);
        value.setMilliseconds(0);
      }

      collection.where( 'name', '==', val?.name ).where( 'value', '==', value ).get().then( query => {
        if ( query.empty ) {

          const uid = firebase.auth().currentUser.uid;
          const batch = firestore.batch();

          const log_audit = firestore.collection('audit').doc();
          const refs = references.source.map(reference => {
            return {
              name: reference.index.name,
              value: reference.index.value
            }
          });

          refs.push({ name: val?.name, value });

          // sanitizeForFirestore: el archivo puede traer `aiEmbedding.__type__`
          // (campo del módulo IA con `__`), que Firestore RECHAZA al escribir el
          // snapshot en audit/before|after y revienta todo el batch. Mismo fix que
          // ya tienen documents.js y trash.js.
          const before = sanitizeForFirestore({ ...doc.data() });
          const after = sanitizeForFirestore({ ...doc.data(), references: refs  });

          batch.set(log_audit, { uid, eventType: `${ DOCUMENTS.UPDATE.UPDATE_DOCUMENTS }_FILES`, eventDate: firestore.FieldValue.serverTimestamp() });
          batch.set(log_audit.collection('before').doc(), before);
          batch.set(log_audit.collection('after').doc(), after);
          // createdAt solo en la subcolección: el elemento del array references[]
          // debe seguir siendo { name, value } exacto para arrayRemove y el buscador.
          batch.set(doc.ref.collection('references').doc(), { name: val?.name, value, createdAt: firestore.FieldValue.serverTimestamp() });
          batch.update(doc.ref, { references: firestore.FieldValue.arrayUnion({ name: val?.name, value }) });

          batch.commit().then(() => {
            loadReferences();
          }).catch( error => {
            notification.error({
              message: 'Error',
              description: 'Se presento un error al tratar de agregar la referencia',
              placement:  'topRight'
            })
          });

        } else notification.warning({
          message: 'Referencia duplicada',
          description: 'La referencia ya fue agregada con el mismo valor.',
          placement:  'topRight'
        });
      });

    }).catch( error => {
      notification.error({
        message: 'Error',
        description: 'Se presento un error al tratar de agregar la referencia',
        placement:  'topRight'
      })
    })
  };

  /**
   * Metodo que permite guardar un usuario aprobador
   */
  const saveApprove = () => {
    if ( approve?.source?.filter( user => user.approve === auth?.user?.email )?.length <= 0 ) {

      const uid = firebase.auth().currentUser.uid;
      const batch = firestore.batch();

      const log_audit = firestore.collection('audit').doc();
      const approves = approve.source.map(data => data.email);

      approves.push(auth?.user?.email);

      const before = sanitizeForFirestore(file.fields);
      const after = sanitizeForFirestore({ ...file.fields, approve: approves  });

      batch.set(log_audit, { uid, eventType: `${ DOCUMENTS.UPDATE.UPDATE_DOCUMENTS }_FILES`, eventDate: firestore.FieldValue.serverTimestamp() });
      batch.set(log_audit.collection('before').doc(), before);
      batch.set(log_audit.collection('after').doc(), after);
      batch.update(file.reference.ref, { approve: firestore.FieldValue.arrayUnion(auth?.user?.email) });
      batch.set(file.reference.ref.collection('approve').doc( auth?.user?.email ), {
        user: firestore.doc(`users/${ auth?.user?.uid }`),
        date_of_approve: new Date()
      })

      batch.commit().then(() => loadApprove() ).catch( error => {
        notification.error({
          message: 'Error',
          description: 'Se presento un error al tratar de aprobar el archivo',
          placement:  'topRight'
        })  
      });

    }
  };

  /**
   * Metodo que permite eliminar un usuario aprobador
   */
  const removeApprove = () => {

    setApprove({ loading: true, source: approve.source });
    file.reference.ref.get().then( fields => {
      
      const data = fields?.data()?.approve?.filter( approve => approve === auth?.user?.email );
      setApprove({ loading: data?.length > 0 ? true : false, source: approve.source });
      if (data?.length <= 0) return;

      const uid = firebase.auth().currentUser.uid;
      const batch = firestore.batch();

      const log_audit = firestore.collection('audit').doc();
      const approves = approve.source.map(data => data.email);

      approves.splice(approves.indexOf(auth?.user?.email), 1);

      const before = sanitizeForFirestore(file.fields);
      const after = sanitizeForFirestore({ ...file.fields, approve: approves  });

      batch.set(log_audit, { uid, eventType: `${ DOCUMENTS.UPDATE.UPDATE_DOCUMENTS }_FILES`, eventDate: firestore.FieldValue.serverTimestamp() });
      batch.set(log_audit.collection('before').doc(), before);
      batch.set(log_audit.collection('after').doc(), after);
      batch.update(file.reference.ref, { approve: firestore.FieldValue.arrayRemove( data[0] ) });
      batch.delete(file.reference.ref.collection('approve').doc( auth?.user?.email ));

      batch.commit().then(() => loadApprove() ).catch( error => {
        setApprove({ loading: false, source: approve.source });
        notification.error({
          message: 'Error',
          description: 'Se presento un error al tratar de desaprobar el archivo',
          placement:  'topRight'
        });
      });

    });

  };

  /**
   * Metodo que permite eliminar una referencia
   * @param {int} index 
   */
  const deleteReference = index => {
    file.reference.ref.get().then( doc => {

      doc.ref.collection('references').where( 'name', '==', index?.name ).where( 'value', '==', index?.value ).get().then( query => {
        if ( !query.empty ) {

          const uid = firebase.auth().currentUser.uid;
          const batch = firestore.batch();

          query.docs.forEach( docRef => {

            const log_audit = firestore.collection('audit').doc();
            const refs = references.source.map(reference => {
              return {
                name: reference.index.name,
                value: reference.index.value
              }
            });

            refs.splice(refs.indexOf({ name: index?.name, value: index?.value }), 1);

            const before = sanitizeForFirestore({ ...doc.data() });
            const after = sanitizeForFirestore({ ...doc.data(), references: refs  });

            batch.set(log_audit, { uid, eventType: `${ DOCUMENTS.UPDATE.UPDATE_DOCUMENTS }_FILES`, eventDate: firestore.FieldValue.serverTimestamp() });
            batch.set(log_audit.collection('before').doc(), before);
            batch.set(log_audit.collection('after').doc(), after);
            batch.delete(docRef.ref);
            batch.update(doc.ref, { references: firestore.FieldValue.arrayRemove({ name: index?.name, value: index?.value }) });

            batch.commit().then(() => {
              loadReferences();
            }).catch( error => {
              notification.error({
                message: 'Error',
                description: 'Se presento un error al tratar de eliminar la referencia',
                placement:  'topRight'
              })
            });

          });

        } else loadReferences();
      });

    });
  };

  const downloadFile = ({ url, name }) => dispatch( filesActions.download( url, name ) );


  // Ejecuta la funcion una vez termina de renderizar el componente
  useEffect(() => { form.resetFields(); }, [form]);
  
  // Ejecuta la funcion cada vez que el estado de referencia cambia
  useEffect(() => {
    if (file?.isLoad && file?.visible && file?.reference) {
      file.reference.ref.get().then( doc => {
        
        const { set, ...rest } = file;
        const fields = doc.data();

        set({ ...rest, fields, isLoad: false });
        setXml({ loading: fields?.xml ? true : false, source: [] }); 

        if (!fields?.xml) return;
        fields.xml.get().then( file => {
          setXml({
            loading: false,
            source: file.exists ? [{
              key: file.id,
              name: file.data().name,
              url: file.data().url,
              date: file.data().date_of_upload.toDate().toLocaleDateString('es-CO', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })
            }] : []
          });
        });

      });
    }
  }, [file]);

  /**
   * Metodo que rederiza el componente
   */
  return file?.visible ? (
    <Drawer
      forceRender
      title = { `Información de Archivo` }
      width = { 780 }
      placement = "left"
      onClose = { onCloseFile }
      maskClosable = { false }
      visible = { file.visible }
      style = {{ height: 'calc(100% - 55px)', overflow: 'auto', paddingBottom: 53 }}
      destroyOnHidden = { true } >

      <Row>
        <Col span = {24} >
          <DescriptionItem title = "Nombre del archivo" content = { file.fields?.name } />
        </Col>
      </Row>
      <Row>
        <Col span = {24} >
          <DescriptionItem
            title = "Fecha de subida"
            content = { file.fields?.date_of_upload?.toDate().toLocaleDateString('es-CO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) }
          />
        </Col>
      </Row>
      {/* La marca es gestión interna entre contadores: la ve quien puede
          contabilizar (ver lib/accounted_visibility.js). */}
      { canSeeAccountedMark({
          accounted: file.fields?.accounted,
          isSuperAdmin: user.isRoot?.data,
          isAdmin: user.isAdmin?.data,
          canAccount: user.roles?.documents?.accounting,
        }) ?
      <Row>
        <Col span = {24} >
          <div style = {{ fontSize: 14, lineHeight: '22px', marginBottom: 7, color: '#52c41a' }} >
            <CheckCircleOutlined style = {{ marginRight: 6 }} />
            <span style = {{ color: 'rgba(0,0,0,0.85)', marginRight: 8 }} > Contabilizado: </span>
            { file.fields.accounted.at?.toDate
              ? file.fields.accounted.at.toDate().toLocaleDateString('es-CO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
              : '' }
            { file.fields.accounted.byName ? ` · por ${ file.fields.accounted.byName }` : '' }
          </div>
        </Col>
      </Row> : null }
      {/* Cols con ancho natural (flex none) en vez de span fijo de 25%:
          con los 4 botones visibles, "Marcar como contabilizado" no cabe
          en un cuarto del drawer y se cortaba. Así cada botón ocupa lo
          suyo y la fila hace wrap si hace falta. */}
      <Row gutter = { [16, 0] } >
        {/* Descargar exige el permiso "Descargar archivo" (documents.download),
            independiente de "Descargar carpeta" (folders.download, solo para el
            ZIP). Leer/previsualizar en pantalla sigue con read. */}
        { user.roles.documents?.download ?
        <Col flex = "none" >
          <Button type = "link" style = {{ paddingLeft: 0 }} onClick = { () => downloadFile({ url: file.fields.url, name: file.fields.name }) } >
            <DownloadOutlined style = {{ marginRight: 8 }} />
            Descargar archivo
          </Button>
        </Col> : null }

        { noToken('document') ?
            <Col flex = "none" >
              <Button type = "link" style = {{ color: '#F5222D', paddingLeft: 0 }}
                onClick = { () => confirmDirect( `Eliminar archivo ${ file.fields?.name }`,
                  () => deleteFile( withNoTokenGrant( 'document', user.roles.documents ) ) ) } >
                <DeleteOutlined style = {{ marginRight: 8 }} />
                Eliminar archivo
              </Button>
            </Col> : null }

        { !noToken('document') && supervisor && user.roles.documents?.delete && !hasAccounting(file.fields?.accounting) && !file.fields?.accounted && closedAccess.folderWrite ?
            <Col flex = "none" >
              <Button
                type = "link"
                style = {{ color: '#F5222D', paddingLeft: 0 }}
                onClick = { () => {
                  Modal.confirm({
                    title: `Eliminar archivo ${ file.fields?.name  }`,
                    content: 'Esta seguro de eliminar el archivo seleccionado?',
                    okText: 'Eliminar',
                    okType: 'danger',
                    cancelText: 'Cancelar',
                    onOk: () => deleteFile()
                  })
                }}
              >
                <DeleteOutlined style = {{ marginRight: 8 }} />
                Eliminar archivo
              </Button>
            </Col> : null }

        { /* Quien no es supervisora ni root pide permiso para eliminar el
             documento (mismo filtro que el borrado directo, evaluado como si
             la carpeta estuviera abierta). La ubicación es la carpeta. */ }
        { !noToken('document') && !supervisor && user.roles.documents?.delete && !hasAccounting(file.fields?.accounting) && !file.fields?.accounted ?
            <Col flex = "none" >
              <RequestDeletionControl
                pending = { !!pendingRequests.get(parentPath) }
                canCancel = { pendingRequests.get(parentPath)?.requestedBy === myUid }
                onRequest = { () => setRequestDelete({
                  kind: 'document',
                  itemName: file.fields?.name,
                  itemPath: parentPath,
                  itemUrl: file.fields?.url || null,
                  parentPath: file.fields?.folder?.path,
                  parentName: null,
                  customerId: extractCustomerId(file.fields?.folder?.path),
                  customerName: file.fields?.customer,
                  tokenRun: null,
                }) }
                onCancelPending = { () => dispatch(deletionRequests.cancel({ id: pendingRequests.get(parentPath).id })).catch(() => {
                  notification.error({ message: 'Error', description: 'No se pudo cancelar la solicitud', placement: 'topRight' });
                }) }
              />
            </Col> : null }

        { user.roles.documents?.read && file.fields?.type === "application/pdf" ?
            <Col flex = "none" >
              <Button
                type = "link"
                style = {{ color: '#FAAD14', paddingLeft: 0 }}
                onClick = { () => setPreview({ ...preview, visible: true }) }
              > <EyeOutlined style = {{ marginRight: 8 }} /> Previsualizar </Button>
            </Col> : null }

        { user.roles.documents?.accounting && closedAccess.drawerWrite ?
            <Col flex = "none" >
              <Button
                type = "link"
                style = {{ color: '#52c41a', paddingLeft: 0 }}
                onClick = { () => {
                  const marking = !file.fields?.accounted;
                  Modal.confirm({
                    title: marking ? 'Marcar como contabilizado' : 'Quitar marca de contabilizado',
                    content: marking
                      ? `¿Marcar "${ file.fields?.name }" como contabilizado con la fecha de hoy?`
                      : `¿Quitar la marca de contabilizado de "${ file.fields?.name }"?`,
                    okText: marking ? 'Marcar' : 'Quitar',
                    okType: marking ? 'primary' : 'danger',
                    cancelText: 'Cancelar',
                    onOk: toggleAccounted
                  })
                }}
              >
                <CheckCircleOutlined style = {{ marginRight: 8 }} />
                { file.fields?.accounted ? 'Quitar marca de contabilizado' : 'Marcar como contabilizado' }
              </Button>
            </Col> : null }

      </Row>

      <Divider />
      
      <Tabs type = "card" onChange = { onChangeTab } >
        <Tabs.TabPane tab = "XML" key = "1" >
          
          <Table
            bordered
            size = 'middle'
            loading = { xml.loading }
            locale = {{
              emptyText: user.roles.documents?.createXml && closedAccess.drawerWrite ?
                <XMLUpload
                  key = "XML"
                  reference = { file.reference }
                  onXML = { loadXML }
                /> : null
            }}
            columns = {[
              { title: 'Nombre del archivo', dataIndex: 'name', key: 'name' },
              { title: 'Fecha de subida', dataIndex: 'date', key: 'date' },
              { title: 'Acciones', key: 'actions', render: ( text, record ) => (
                <span>
                  { user.roles.documents?.read ?
                    <Button type = "link" onClick = { () => downloadFile({ url: record?.url, name: record?.name }) } > Descargar </Button> : null }
                  { noToken('xml') ?
                    <>
                      <Divider type = "vertical" />
                      <Button type = "link" onClick = { () => confirmDirect( `Eliminar XML ${ record?.name }`,
                        () => deleteXML( withNoTokenGrant( 'xml', user.roles.documents ) ) ) } > Eliminar </Button>
                    </> : null }
                  { !noToken('xml') && supervisor && user.roles.documents?.deleteXml && closedAccess.drawerDelete ?
                    <>
                      <Divider type = "vertical" />
                      <Button
                        type = "link"
                        onClick = { () => setTokenDelete({
                          itemType: DELETION_ITEM_TYPES.XML,
                          itemName: record?.name,
                          run: () => deleteXML(),
                        }) }
                      > Eliminar </Button>
                    </> : null }
                  { !noToken('xml') && !supervisor && user.roles.documents?.deleteXml ?
                    renderRequestControl( requestFor({
                      kind: 'xml',
                      record,
                      itemPath: `files/${ record?.key }`,
                      tokenItemType: DELETION_ITEM_TYPES.XML,
                      tokenRun: () => deleteXML(),
                    }) ) : null }
                </span>
              )}
            ]}
            dataSource = { xml.source }
            pagination = {{ defaultPageSize: 4 }}
          />

        </Tabs.TabPane>
        <Tabs.TabPane tab = "Contabilización" key = "2" >

          { /* Una sola contabilización por documento (lib/accounting_refs.js).
               El botón solo aparece si no tiene ninguna, y después de cargar la
               lista (si no, aparece un instante y desaparece). El thunk vuelve
               a comprobarlo antes de subir. */ }
          { user.roles.documents?.accounting && closedAccess.drawerWrite && !accounting.loading ?
            ( accounting.source.length === 0 ?
              <div style = {{ marginBottom: 16 }} >
                <AccountingUpload
                  key = "accounting"
                  reference = { file.reference }
                  onAccounting = { loadAccounting }
                />
              </div>
            :
              <div style = {{ marginBottom: 16, color: 'rgba(0,0,0,0.55)', fontSize: 13 }} >
                { ONE_ACCOUNTING_MESSAGE }
              </div>
            ) : null }

          <Table
            bordered
            size = 'middle'
            loading = { accounting.loading }
            locale = {{ emptyText: 'Este documento no tiene contabilizaciones' }}
            columns = {[
              { title: 'Nombre del archivo', dataIndex: 'name', key: 'name' },
              { title: 'Fecha contable', dataIndex: 'date', key: 'date' },
              { title: 'Acciones', key: 'actions', render: ( text, record ) => (
                <span>
                  { user.roles.documents?.read ?
                    <Button type = "link" onClick = { () => downloadFile({ url: record?.url, name: record?.name }) } > Descargar </Button> : null }
                  { user.roles.documents?.read ?
                    <>
                      <Divider type = "vertical" />
                      { /* PDF → visor embebido; otros tipos → pestaña nueva
                           (el navegador decide cómo mostrarlos) */ }
                      <Button type = "link"
                        onClick = { () => record?.type === 'application/pdf'
                          ? setAccPreview({ visible: true, url: record.url, name: record.name })
                          : window.open( record?.url, '_blank', 'noopener' ) }
                      > Previsualizar </Button>
                    </> : null }
                  { noToken('accounting') ?
                    <>
                      <Divider type = "vertical" />
                      <Button type = "link" onClick = { () => confirmDirect( `Reversar contabilización ${ record?.name }`,
                        () => accountingThrowBack( record, withNoTokenGrant( 'accounting', user.roles.documents ) ) ) } > Eliminar </Button>
                    </> : null }
                  { !noToken('accounting') && supervisor && canDeleteAccounting({ globalRole, effective: user.roles, isClosing: isClosing.data }) ?
                    <>
                      <Divider type = "vertical" />
                      <Button
                        type = "link"
                        onClick = { () => setTokenDelete({
                          itemType: DELETION_ITEM_TYPES.ACCOUNTING,
                          itemName: record?.name,
                          run: () => accountingThrowBack( record ),
                        }) }
                      > Eliminar </Button>
                    </> : null }
                  { !noToken('accounting') && !supervisor && canDeleteAccounting({ globalRole, effective: user.roles, isClosing: false }) ?
                    renderRequestControl( requestFor({
                      kind: 'accounting',
                      record,
                      itemPath: record?.ref?.path,
                      tokenItemType: DELETION_ITEM_TYPES.ACCOUNTING,
                      tokenRun: () => accountingThrowBack( record ),
                    }) ) : null }
                </span>
              )}
            ]}
            dataSource = { accounting.source }
            pagination = {{ defaultPageSize: 4 }}
          />

        </Tabs.TabPane>
        <Tabs.TabPane tab = "Referencias" key = "3" >
          
          { user.roles.documents?.createReference && closedAccess.drawerWrite ?
          <Button
            loading = { references.loading }
            onClick = { openDrawerReference }
            type = "primary"
            style = {{ marginBottom: 16 }} >
            Agregar referencia
          </Button> : null }

          <Table
            bordered
            size = 'middle'
            loading = { references.loading }
            locale = {{ emptyText: 'No se han agregado referencias' }}
            columns = {[
              { title: 'Referencia', dataIndex: 'ref', key: 'ref' },
              { title: 'Valor', dataIndex: 'value', key: 'value' },
              ...( showRegistrationDate ? [{ title: 'Fecha de registro', dataIndex: 'date', key: 'date' }] : [] ),
              { title: 'Acciones', key: 'actions', render: ( text, record ) => (
                <span>
                  { user.roles.documents?.deleteReference && closedAccess.drawerWrite ?
                      <Popconfirm
                        title = "Esta seguro de eliminar la referencia？"
                        okText = "Eliminar"
                        cancelText = "Cancelar"
                        onConfirm = { () => deleteReference( record?.index ) }
                      >
                        <Button type = "link" > Eliminar </Button>
                      </Popconfirm> : null }
                </span>
              )}
            ]}
            dataSource = { references.source }
            pagination = {{ defaultPageSize: 4 }}
          />

        </Tabs.TabPane>
        <Tabs.TabPane tab = "Archivos vinculados" key = "4" >
        
          { user.roles.documents?.createLink && closedAccess.drawerWrite ?
              <LinkUpload
                key = "linking"
                reference = { file.reference }
                loading = { links.loading }
                onLinking = { loadLinksFiles }
                onPreview = { () => setLinks({ loading: true, source: links.source }) }
              /> : null }

          <Table
            bordered
            size = 'middle'
            loading = { links.loading }
            locale = {{ emptyText: 'No se han vinculado archivos' }}
            columns = {[
              { title: 'Nombre del archivo', dataIndex: 'name', key: 'name' },
              ...( showRegistrationDate ? [{ title: 'Fecha de registro', dataIndex: 'date', key: 'date' }] : [] ),
              { title: 'Acciones', key: 'actions', render: ( text, record ) => (
                <span>
                  { user.roles.documents?.read ?
                    <Button type = "link" onClick = { () => downloadFile({ url: record?.url, name: record?.name }) } > Descargar </Button> : null }
                  { noToken('linked') ?
                    <>
                      <Divider type = "vertical" />
                      <Button type = "link" onClick = { () => confirmDirect( `Eliminar vinculado ${ record?.name }`,
                        () => deleteLinkFile( record?.ref, withNoTokenGrant( 'linked', user.roles.documents ) ) ) } > Eliminar </Button>
                    </> : null }
                  { !noToken('linked') && supervisor && canDeleteLinked({ globalRole, effective: user.roles, isClosing: isClosing.data, hasAccounting: hasAccounting(file.fields?.accounting) }) ?
                    <>
                      <Divider type = "vertical" />
                      <Button
                        type = "link"
                        onClick = { () => setTokenDelete({
                          itemType: DELETION_ITEM_TYPES.LINK,
                          itemName: record?.name,
                          run: () => deleteLinkFile( record?.ref ),
                        }) }
                      > Eliminar </Button>
                    </> : null }
                  { !noToken('linked') && !supervisor && canDeleteLinked({ globalRole, effective: user.roles, isClosing: false, hasAccounting: hasAccounting(file.fields?.accounting) }) ?
                    renderRequestControl( requestFor({
                      kind: 'link',
                      record,
                      itemPath: record?.ref?.path,
                      tokenItemType: DELETION_ITEM_TYPES.LINK,
                      tokenRun: () => deleteLinkFile( record?.ref ),
                    }) ) : null }
                </span>
              )}
            ]}
            dataSource = { links.source }
            pagination = {{ defaultPageSize: 4 }}
          />
        
        </Tabs.TabPane>
        <Tabs.TabPane tab = "Aprobación de archivos" key = "5" >
          
          { (() => {
              const isApprover = approve.source?.filter( u => u?.email === auth?.user?.email )?.length > 0;
              const canApprove = user.roles.documents?.update;
              const canDisapprove = user.roles.documents?.disapprove;
              const allowed = isApprover ? canDisapprove : canApprove;
              return auth && allowed ?
                <Button
                  loading = { approve?.loading }
                  onClick = { isApprover ? removeApprove : saveApprove }
                  type = "primary"
                  style = {{ marginBottom: 16 }}
                >
                  { isApprover ? 'Desaprobar archivo' : 'Aprobar archivo' }
                </Button> : null;
          })() }

          <Table
            bordered
            size = 'middle'
            loading = { approve.loading }
            locale = {{ emptyText: 'Este archivo no ha sido aprobado por ningún usuario' }}
            columns = {[
              { title: 'Usuario', dataIndex: 'name', key: 'name' },
              { title: 'Email', dataIndex: 'email', key: 'email' },
              { title: 'Fecha de aprobación', dataIndex: 'date', key: 'date' },
            ]}
            dataSource = { approve.source }
            pagination = {{ defaultPageSize: 4 }}
          />
        </Tabs.TabPane>
      </Tabs>

      <Drawer
        forceRender
        title = 'Nueva referencia'
        width = { 320 }
        placement = "left"
        onClose = { closeDrawerReference }
        maskClosable = { false }
        visible = { references.visible }
        style = {{ height: 'calc(100% - 55px)', overflow: 'auto', paddingBottom: 53 }}
        destroyOnHidden = { true } >

        { read?.type === REFERENCES.READ.READED_REFERENCE ?
            <Form form = { form } layout = "vertical" onFinish = { saveReference } >
              <Form.Item name = "reference" label = "Referencia"
                rules = {[{ required: true, message: 'La referencia es requerido' }]} >
                <Select placeholder = "Seleccione una referencia" onChange = { handleSelectChangeReference }
                  rules = {[{ required: true, message: 'La referencia es requerido' }]} >
                  {
                    read?.reference?.map( ref => <Select.Option key = { ref?.id } value = { ref?.id } > { ref?.name } </Select.Option> )
                  }
                </Select>
              </Form.Item>
              <Form.Item name = "value" label = "Valor de referencia"
                rules = {[
                  { required: true, message: 'El valor de referencia es requerido' },
                  { type: selected.type, message: selected.message }
                ]} >
                { selected.component ? selected.component : <Input style = {{ width: '100%' }} /> }
              </Form.Item>
              <Form.Item>
                <Button type = "primary" onClick = { () => form.submit() } >
                  Agregar
                </Button>
              </Form.Item>
            </Form> : null }

      </Drawer>

      {/* Los dos visores usan el MISMO componente: antes el del soporte
          contable montaba un <Viewer> pelado y por eso no tenia barra de
          botones. Ver PdfPreviewDrawer arriba. */}
      { file.fields?.type === "application/pdf" ?
          <PdfPreviewDrawer
            visible = { preview.visible }
            title = { file.fields?.name || 'Previsualización' }
            url = { file.fields?.url }
            onClose = { () => setPreview({ ...preview, visible: false }) } /> : null }

      { /* Previsualización de un archivo de contabilización (PDF) */ }
      <PdfPreviewDrawer
        visible = { accPreview.visible }
        title = { accPreview.name || 'Previsualización' }
        url = { accPreview.url }
        onClose = { () => setAccPreview({ visible: false, url: null, name: '' }) } />

      { /* Confirmación con token para contabilización, vinculados y XML.
          Una sola instancia: `tokenDelete` dice qué se borra y cómo. */ }
      <DeleteTokenModal
        open = { !!tokenDelete }
        itemType = { tokenDelete?.itemType }
        itemName = { tokenDelete?.itemName }
        customerId = { extractCustomerId( file.fields?.folder?.path ) }
        customerName = { file.fields?.customer }
        onCancel = { () => setTokenDelete( null ) }
        onConfirmed = { () => tokenDelete?.run?.() } />

      { /* Solicitud de eliminación: quien no es supervisora pide permiso. */ }
      <RequestDeletionModal
        open = { !!requestDelete }
        request = { requestDelete }
        onCancel = { () => setRequestDelete( null ) }
        onSent = { () => {
          setRequestDelete( null );
          notification.success({ message: 'Solicitud enviada', description: 'Una supervisora la revisará.', placement: 'topRight' });
        } }
        onUseToken = { requestDelete?.tokenRun ? () => {
          const r = requestDelete;
          setRequestDelete( null );
          setTokenDelete({ itemType: r.tokenItemType, itemName: r.itemName, run: r.tokenRun });
        } : null } />

    </Drawer>
  ) : null;
};